/* eslint-disable jsx-a11y/img-redundant-alt */
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from 'react-helmet';
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import car from "../../src/images/service/car.png";
import Navbar from "../components/navbar";
import Footer from "../homeComponents/footer";
import toast from "react-hot-toast";

const PHONE_PATTERN = /^[0-9]{10}$/;

// Small, reusable label helpers so the required/optional marking convention
// stays identical across both steps instead of being retyped per field.
const RequiredLabel = ({ children }) => (
  <>
    {children} <span className="text-[#ADD144]">*</span>
  </>
);

const OptionalLabel = ({ children }) => (
  <>
    {children} <span className="text-[#F0FFC5] text-opacity-80 font-normal">(optional)</span>
  </>
);

// One shared toggle-button pattern for every "select one of two" control on
// the page (Vehicle Type, Dealer Type) - same height, padding, radius,
// border, and font weight regardless of which group renders it.
const toggleButtonClass = (selected) =>
  `font-semibold rounded-full px-8 py-3 w-full sm:w-auto transition-all duration-200 active:scale-[0.97] border ${
    selected
      ? "bg-[#ADD144] text-[#1F1F1F] border-transparent"
      : "bg-transparent border-[#718929] text-[#F0FFC5] text-opacity-80"
  }`;

const Service = () => {
  const location = useLocation();
  const [selectedButton, setSelectedButton] = useState(""); // Button state
  const [inspectionType, setInspectionType] = useState(""); // Inspection type state
  const [vehicleType, setVehicleType] = useState("Luxury");
  const [carImage, setCarImage] = useState("");
  const [formData, setFormData] = useState({
    customerName: "",
    customerPhone: "",
    vehicleModel: "",
    vehicleMake: "",
    variant: "",
    address: "",
    allottedVin: "",
    deliveryDate: "",
    dealerType: "",
    customMessage: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  // Ref (not state) so a second submit fired before the isLoading re-render
  // lands - a fast double-click, an Enter-key + click race - is still caught
  // synchronously. State alone leaves a gap: it only blocks the *next*
  // render's disabled button, not a second handleSubmit call that starts
  // before that render happens.
  const isSubmittingRef = useRef(false);

  // Two-step presentation state. Purely presentational - does not change
  // what's collected or how it's submitted, only how it's grouped on screen.
  const [currentStep, setCurrentStep] = useState(1);
  const [stepDirection, setStepDirection] = useState("forward");

  const API_URL = process.env.REACT_APP_API_URL;

  // Extract the query parameter
  useEffect(() => {
    // Scroll to the top of the page on component mount
    window.scrollTo(0, 0);

    // Extract the query parameter for inspectionType
    const params = new URLSearchParams(location.search);
    const inspectionTypeParam = params.get("inspectionType");
    // console.log("Decoded inspectionTypeParam:", inspectionTypeParam); // Debug
    if (inspectionTypeParam) {
      setInspectionType(inspectionTypeParam);
    }
  }, []); // Empty dependency array ensures this runs only once

  // Amounts for each inspection type
  const inspectionAmounts = {
    "Pre Delivery Inspection": {
      Luxury: 7999,
      Ordinary: 1999,
    },
    "Used Car Inspections": {
      Luxury: 2499,
      Ordinary: 1499,
    },
    "Pre Trip Inspections": {
      Luxury: 999,
      Ordinary: 499,
    },
  };

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user); // Get the entire user object
  const userId = user ? user.id : null; // Safely access user.id
  const navigate = useNavigate();

  // Function to fetch car image when vehicle model changes
  const fetchCarImage = async (vehicleModel) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/cars/image?model=${vehicleModel}`
      );
      // console.log("Backend response:", response.data); // Log the response data to see the structure

      if (
        response.data &&
        response.data.images &&
        response.data.images.length > 0
      ) {
        // Filter images to find only PNG images by checking mime type
        const pngImages = response.data.images.filter(
          (image) => image.mime === "image/png"
        );

        if (pngImages.length > 0) {
          const firstImage = pngImages[0]; // Get the first PNG image
          setCarImage(firstImage.link); // Assuming 'link' is the field holding the image URL
        } else {
          console.error("No PNG images found in the response.");
        }
      } else {
        console.error("No images found in the response.");
      }
    } catch (error) {
      console.error("Error fetching car image: ", error);
    }
  };

  useEffect(() => {
    if (userId) {
      const fetchUserPhoneNumber = async () => {
        try {
          const response = await axios.get(
            `${API_URL}/api/users/get-phone/${userId}`
          );
          const userPhone = response.data.phoneNumber; // Assuming the field is phoneNumber

          if (userPhone) {
            // If phone number exists, set it in formData
            setFormData((prevData) => ({
              ...prevData,
              customerPhone: userPhone,
            }));
          } else {
            // If no phone number exists, do nothing for now
          }
        } catch (error) {
          console.error("Error fetching user phone number:", error);
        }
      };

      fetchUserPhoneNumber();
    }
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear a shown phone error as soon as the value becomes valid again,
    // rather than making the user blur out and back in to dismiss it.
    if (name === "customerPhone" && phoneError && PHONE_PATTERN.test(value)) {
      setPhoneError("");
    }

    // Fetch car image when vehicle model changes
    // if (name === "vehicleModel") {
    //   fetchCarImage(value);
    // }
  };

  // Inline validation on blur (not per-keystroke): only flags the phone
  // field once the user has actually finished with it.
  const handlePhoneBlur = () => {
    if (formData.customerPhone && !PHONE_PATTERN.test(formData.customerPhone)) {
      setPhoneError("Enter a valid 10-digit phone number.");
    } else {
      setPhoneError("");
    }
  };

  const handleButtonClick = (buttonName) => {
    setSelectedButton(buttonName); // Update selected button
    // Set the dealerType based on the selected button
    setFormData((prev) => ({
      ...prev,
      dealerType: buttonName === "dealerShowroom" ? "Showroom" : "Stockyard",
    }));
  };

  // Step 1 covers exactly what the backend actually requires to make sense
  // of a booking before contact details are worth asking for: which
  // inspection, and which vehicle. Vehicle Type / Dealer Type stay
  // presentation-only toggles (as they always were - neither was in the
  // original required-fields check either), so they don't gate advancing.
  const isStep1Valid =
    !!inspectionType &&
    !!formData.vehicleMake &&
    !!formData.vehicleModel &&
    !!formData.variant;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Reset scroll on every step change - Step 1 and Step 2 have different
  // content heights, so without this a scrolled-down user could land under
  // the fixed navbar after advancing/going back.
  const scrollFormIntoView = () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  const goToStep2 = () => {
    if (!isStep1Valid) return;
    setStepDirection("forward");
    setCurrentStep(2);
    scrollFormIntoView();
  };

  const goToStep1 = () => {
    setStepDirection("backward");
    setCurrentStep(1);
    scrollFormIntoView();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsLoading(true);

    // Validate required fields
    const requiredFields = [
      "customerName",
      "customerPhone",
      "vehicleMake",
      "vehicleModel",
      "variant",
      "address",
      "deliveryDate",
    ];

    const missingField = requiredFields.find(field => !formData[field]);
    if (missingField) {
      toast.error("Please fill in all required fields.");
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    // Validate phone number
    if (!PHONE_PATTERN.test(formData.customerPhone)) {
      setPhoneError("Enter a valid 10-digit phone number.");
      toast.error("Invalid phone number. Enter a valid 10-digit number.");
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    let existingPhoneNumber = null;

    // Try fetching existing phone
    if (userId) {
      try {
        const response = await axios.get(`${API_URL}/api/users/get-phone/${userId}`);
        existingPhoneNumber = response?.data?.phoneNumber || null;
      } catch (err) {
        if (err.response?.status === 404) {
          console.warn("User not found. Proceeding without phone check.");
        } else {
          console.error("Phone number fetch error:", err);
          toast.error("Error checking existing phone number.");
          setIsLoading(false);
          return;
        }
      }
    }

    // Save new phone number if different
    try {
      if (formData.customerPhone && formData.customerPhone !== existingPhoneNumber) {
        await axios.post(`${API_URL}/api/users/save-phone`, {
          userId,
          phoneNumber: formData.customerPhone,
        });
      }
    } catch (error) {
      console.error("Phone save error:", error);
      toast.error("Error saving phone number.");
      setIsLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    // Try creating the service
    try {
      const serviceResponse = await axios.post(`${API_URL}/api/services/create`, {
        inspectionType,
        vehicleType: vehicleType || "",
        userId,
        ...formData,
      });

      if (serviceResponse.status === 200) {
        toast.success("Service created successfully!");
        navigate("/progress", {
          state: { inspectionType, userId, ...formData },
        });
      } else {
        toast.error("Service creation failed.");
      }
    } catch (error) {
      console.error("Service creation error:", error);
      toast.error("Error in service creation: " + (error?.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const inputClass =
    "border rounded-3xl w-full py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#ADD144] focus:border-[#ADD144] transition text-[#F0FFC5]";
  const labelClass =
    "absolute left-4 -top-3 bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1";

  const stepAnimClass =
    stepDirection === "forward"
      ? "step-enter-forward"
      : "step-enter-backward";

  return (
    <div className="bg-banner w-full min-h-screen font-clash">
      <Helmet>
        <title>Service</title>
        <meta
          name="keywords"
          content="Pdi, Bengaluru, Bangalore, Kardo, Inspection, Ze, Pre delivery, Services, Car, Cars, Vaidya, BHP, Team, CID, Expert, In, Best, 24, BOSS, Dail, Just, New car, Checklist, Detailed, Report, Inspect, Reliable, Blog, Used car inspection, Buying a new car"
        />
        <meta
          name="description"
          content="Explore our reliable pre-delivery inspection services for new and used cars. Detailed reports, expert insights, and the best service in Bangalore and beyond."
        />
      </Helmet>
      {/* Step-transition + reduced-motion handling in one small, unscoped
          style block - matching the existing pattern already used in this
          file for the date-input picker icon color below. */}
      <style jsx>
        {`
          @keyframes stepEnterForward {
            from { opacity: 0; transform: translateX(24px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes stepEnterBackward {
            from { opacity: 0; transform: translateX(-24px); }
            to { opacity: 1; transform: translateX(0); }
          }
          .step-enter-forward { animation: stepEnterForward 250ms ease-out; }
          .step-enter-backward { animation: stepEnterBackward 250ms ease-out; }

          .stepper-connector-fill {
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 250ms ease-out;
          }
          .stepper-connector-fill.filled {
            transform: scaleX(1);
          }

          @media (prefers-reduced-motion: reduce) {
            .step-enter-forward, .step-enter-backward {
              animation: none;
            }
            .stepper-connector-fill {
              transition: none;
            }
          }
        `}
      </style>
      <Navbar />
      {/* Check for authentication and userId */}
      {!isAuthenticated || !userId ? (
        <div className="flex items-center justify-center text-center bg-banner min-h-screen text-[#FFFF]">Please login to continue</div>
      ) : (
        <div className="flex items-center justify-center lg:pt-10">
          <div className="lg:max-w-lg mx-auto p-4 w-full">
            <h1 className="text-[42px] text-[#FFFF] font-semibold mb-6 pt-28">
              <span>Hey, {user ? user.firstname : "User"}</span>
            </h1>

            {/* Stepper - left-aligned to the form column like everything else
                on the page, not centered independently of it. Both pills
                share identical box model (padding, border) in every state
                so they sit on the same baseline and the connector lands
                exactly between them. */}
            <div className="flex items-center w-full mb-8">
              <div
                className={`flex items-center justify-center rounded-full font-semibold transition-colors duration-300 w-9 h-9 text-sm sm:w-auto sm:h-auto sm:px-5 sm:py-2 border border-transparent ${
                  currentStep === 1
                    ? "bg-[#ADD144] text-[#1F1F1F]"
                    : "bg-[#191F09] text-[#ADD144]"
                }`}
              >
                {currentStep > 1 ? "✓" : "1"}
                <span className="hidden sm:inline sm:ml-2">Vehicle &amp; Inspection</span>
              </div>

              <div className="flex-1 h-[2px] mx-3 bg-[#718929] bg-opacity-50 overflow-hidden">
                <div
                  className={`h-full bg-[#ADD144] stepper-connector-fill ${
                    currentStep === 2 ? "filled" : ""
                  }`}
                />
              </div>

              <div
                className={`flex items-center justify-center rounded-full font-semibold transition-colors duration-300 w-9 h-9 text-sm sm:w-auto sm:h-auto sm:px-5 sm:py-2 border ${
                  currentStep === 2
                    ? "bg-[#ADD144] text-[#1F1F1F] border-transparent"
                    : "bg-transparent text-[#F0FFC5] text-opacity-80 border-[#718929]"
                }`}
              >
                2
                <span className="hidden sm:inline sm:ml-2">Your Details</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 pb-32">
              {currentStep === 1 && (
                <div key="step1" className={stepAnimClass}>
                  <p className="text-[#F0FFC5] text-opacity-80 text-sm font-medium mb-6">
                    * Required field
                  </p>

                  <div className="relative mb-6">
                    <label className={labelClass}>
                      <RequiredLabel>Inspection Type</RequiredLabel>
                    </label>
                    <select
                      value={inspectionType}
                      onChange={(e) => setInspectionType(e.target.value)}
                      className="appearance-none block w-full p-3 pr-10 rounded-3xl border border-[#F0FFC5] bg-transparent text-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#ADD144] focus:border-[#ADD144] transition"
                    >
                      <option value="" className="bg-[#1F1F1F]">Select an inspection</option>
                      <option value="Pre Delivery Inspection" className="bg-[#1F1F1F]">
                        Pre Delivery Inspection
                      </option>
                      <option value="Used Car Inspections" className="bg-[#1F1F1F]">Used Car Inspections</option>
                      <option value="Pre Trip Inspections" className="bg-[#1F1F1F]">Pre Trip Inspections</option>
                    </select>
                    <FaChevronDown
                      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#ADD144]"
                      size={14}
                    />
                  </div>

                  {inspectionType === "Pre Delivery Inspection" && (
                    <div className="mb-6">
                      <p className="text-[#F0FFC5] text-opacity-80 text-sm font-medium mb-3">
                        Vehicle Type <span className="text-[#ADD144]">*</span> Select one
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 w-full">
                        <button
                          type="button"
                          onClick={() => setVehicleType("Luxury")}
                          className={`${toggleButtonClass(vehicleType === "Luxury")} flex-1`}
                        >
                          Luxury
                        </button>
                        <button
                          type="button"
                          onClick={() => setVehicleType("Ordinary")}
                          className={`${toggleButtonClass(vehicleType === "Ordinary")} flex-1`}
                        >
                          Ordinary
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row md:space-x-4 w-full">
                    <div className="relative mb-6 md:mb-0 flex-1 min-w-0">
                      <label className={labelClass}>
                        <RequiredLabel>Vehicle Make</RequiredLabel>
                      </label>
                      <input
                        type="text"
                        name="vehicleMake"
                        value={formData.vehicleMake}
                        onChange={handleChange}
                        required
                        className={inputClass}
                      />
                    </div>

                    <div className="relative mb-6 md:mb-0 flex-1 min-w-0">
                      <label className={labelClass}>
                        <RequiredLabel>Vehicle Model</RequiredLabel>
                      </label>
                      <input
                        type="text"
                        name="vehicleModel"
                        value={formData.vehicleModel}
                        onChange={handleChange}
                        required
                        className={inputClass}
                      />
                    </div>

                    <div className="relative mb-6 flex-1 min-w-0">
                      <label className={labelClass}>
                        <RequiredLabel>Variant</RequiredLabel>
                      </label>
                      <input
                        type="text"
                        name="variant"
                        value={formData.variant}
                        onChange={handleChange}
                        required
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {inspectionType === "Pre Delivery Inspection" && (
                    <div className="mb-6">
                      <p className="text-[#F0FFC5] text-opacity-80 text-sm font-medium mb-3">
                        Dealer Type <span className="text-[#ADD144]">*</span> Select one
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 w-full">
                        <button
                          type="button"
                          onClick={() => handleButtonClick("dealerShowroom")}
                          className={`${toggleButtonClass(selectedButton === "dealerShowroom")} flex-1`}
                        >
                          Dealer Showroom
                        </button>
                        <button
                          type="button"
                          onClick={() => handleButtonClick("dealerStockyard")}
                          className={`${toggleButtonClass(selectedButton === "dealerStockyard")} flex-1`}
                        >
                          Dealer Stockyard
                        </button>
                      </div>
                    </div>
                  )}

                  {inspectionType && vehicleType && (
                    <div
                      key={`${inspectionType}-${vehicleType}`}
                      className="text-[#ADD144] font-semibold mb-6 step-enter-forward"
                    >
                      Amount to be paid: Rs{" "}
                      {(inspectionAmounts[inspectionType] && inspectionAmounts[inspectionType][vehicleType]) || 0}
                    </div>
                  )}

                  {/* Full-width on mobile, matching Register's mobile width
                      exactly (same reasoning as the Back/Register stack).
                      Capped to 200px and right-aligned only from sm: up,
                      where the column is fixed-width and the offset reads
                      as deliberate rather than a lone button adrift with
                      dead space to its left. */}
                  <div className="flex items-center w-full justify-center sm:justify-end mt-6">
                    <button
                      type="button"
                      onClick={goToStep2}
                      disabled={!isStep1Valid}
                      className={`w-full sm:max-w-[200px] px-8 py-3 rounded-full font-semibold transition-all duration-200 active:scale-[0.97] border ${
                        isStep1Valid
                          ? "bg-[#ADD144] text-[#1F1F1F] border-transparent"
                          : "bg-transparent border-[#718929] text-[#F0FFC5] text-opacity-40 cursor-not-allowed"
                      }`}
                    >
                      Next Step
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div key="step2" className={stepAnimClass}>
                  <p className="text-[#F0FFC5] text-opacity-80 text-sm font-medium mb-6">
                    * Required field
                  </p>

                  <div className="relative mb-6">
                    <label className={labelClass}>
                      <RequiredLabel>Customer Name</RequiredLabel>
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      required
                      className={inputClass}
                    />
                  </div>

                  <div className="relative mb-6">
                    <label className={labelClass}>
                      <RequiredLabel>Customer Phone</RequiredLabel>
                    </label>
                    <input
                      type="tel"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleChange}
                      onBlur={handlePhoneBlur}
                      required
                      className={inputClass}
                    />
                    {phoneError && (
                      <p className="text-[#FFBD6F] text-sm font-medium mt-2">{phoneError}</p>
                    )}
                  </div>

                  <div className="relative mb-6">
                    <label className={labelClass}>
                      <RequiredLabel>Address</RequiredLabel>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className={inputClass}
                    />
                  </div>

                  <div className="flex flex-col md:flex-row md:space-x-4 w-full">
                    <div className="relative mb-6 md:mb-0 flex-1 min-w-0">
                      <label className={labelClass}>
                        <OptionalLabel>Allotted VIN</OptionalLabel>
                      </label>
                      <input
                        type="text"
                        name="allottedVin"
                        value={formData.allottedVin}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>

                    <div className="relative mb-6 flex-1 min-w-0">
                      <label className={labelClass}>
                        <RequiredLabel>Inspection Date</RequiredLabel>
                      </label>
                      <input
                        type="date"
                        name="deliveryDate"
                        value={formData.deliveryDate}
                        onChange={handleChange}
                        required
                        className={`${inputClass} appearance-none`}
                      />
                    </div>
                  </div>

                  <div className="relative mb-6">
                    <label className={labelClass}>
                      <OptionalLabel>Message</OptionalLabel>
                    </label>
                    <input
                      type="text"
                      name="customMessage"
                      value={formData.customMessage}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>

                  {/* Bookended (Back left, Register right) from sm: up. On
                      mobile the row collapses to a stacked column, so
                      justify-between has no visible effect there - both
                      buttons are already full-width and centered by the
                      stack itself, which is exactly what we want on a
                      narrow screen. */}
                  <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 mt-6">
                    <button
                      type="button"
                      onClick={goToStep1}
                      className={`w-full sm:w-auto ${toggleButtonClass(false)}`}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className={`w-full sm:w-auto ${toggleButtonClass(true)} disabled:opacity-50 disabled:cursor-not-allowed`}
                      disabled={isLoading}
                    >
                      {isLoading ? "Registering..." : "Register"}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Right Section */}
          <div className=" items-center justify-center lg:flex hidden">
            <img
              src={carImage || car}
              alt="image of a car"
              className="max-h-[600px] xl:max-h-[1200px] max-w-[600px] xl:max-w-[1200px] w-auto mb-28 "
            />
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Service;
